<template>
  <header class="flex items-center gap-2 bg-gray-200 px-2.5 py-2.5">
    <button type="button" class="rounded-full p-1 text-gray-700 hover:bg-gray-300" title="Retour" @click="emit('back')">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
    </button>
    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">IA</span>
    <span class="min-w-0 flex-1">
      <span class="block truncate text-sm font-bold text-gray-900">Assistant IA</span>
      <span class="block truncate text-[11px] text-gray-600">{{ model || 'Modèle non configuré' }}</span>
    </span>
    <button v-if="canView" type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Nouvelle conversation" :disabled="sending" @click="newConversation">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
    </button>
    <button v-if="canView && conversationId" type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Supprimer la conversation" :disabled="sending" @click="removeConversation">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.87 12.14A2 2 0 0116.14 21H7.86a2 2 0 01-1.99-1.86L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" /></svg>
    </button>
    <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Fermer" @click="emit('close')">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
    </button>
  </header>

  <div ref="scrollEl" class="flex-1 space-y-2 overflow-y-auto bg-white px-3 py-3">
    <p v-if="!messages.length && !pending" class="p-6 text-center text-sm text-gray-500">Aucun message.</p>
    <div v-for="m in messages" :key="m.id" class="flex" :class="m.role === 'USER' ? 'justify-end' : 'justify-start'">
      <div
        v-if="m.role === 'USER'"
        class="max-w-[85%] rounded-2xl bg-blue-600 px-3 py-2 text-sm text-white flex flex-col gap-1"
      >
        <div v-if="m.content" class="whitespace-pre-wrap break-words">{{ m.content }}</div>
        <div v-if="m.attachments && m.attachments.length" class="flex flex-wrap gap-1">
          <img
            v-for="(att, i) in m.attachments"
            :key="i"
            :src="att.url"
            :alt="att.name || 'image'"
            class="max-h-48 max-w-[200px] rounded-lg border border-white/30 object-cover"
          />
        </div>
      </div>
      <div
        v-else
        class="w-full max-w-full break-words rounded-2xl bg-gray-200 px-3 py-2 text-sm text-gray-900 flex flex-col gap-1"
      >
        <MarkdownContent :content="m.content" />
        <div v-if="m.attachments && m.attachments.length" class="flex flex-wrap gap-1">
          <img
            v-for="(att, i) in m.attachments"
            :key="i"
            :src="att.url"
            :alt="att.name || 'image'"
            class="max-h-64 max-w-[260px] rounded-lg border border-gray-300 object-contain bg-white"
          />
        </div>
      </div>
    </div>
    <div v-if="pending" class="flex justify-start">
      <div class="flex items-center gap-1.5">
        <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0s;"></div>
        <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0.2s;"></div>
        <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0.4s;"></div>
      </div>
    </div>
    <div v-if="errorText" class="flex justify-start">
      <div class="max-w-[85%] break-words rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{{ errorText }}</div>
    </div>
  </div>

  <form v-if="canView" class="flex items-end gap-2 border-t border-gray-300 bg-gray-100 p-2" @submit.prevent="send">
    <textarea
      v-model="draft"
      rows="1"
      maxlength="8000"
      placeholder="Écrire à l'assistant…"
      class="max-h-28 min-h-[2.25rem] flex-1 resize-none rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      @keydown.enter.exact.prevent="send"
    />
    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg,image/gif,image/webp"
      multiple
      @change="onFilesSelected"
      class="hidden"
    />
    <button
      type="button"
      class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300 disabled:opacity-50" :disabled="sending"
      title="Joindre une image"
      @click="fileInput?.click()"
    >
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
    </button>
    <div v-if="attachments.length" class="flex flex-wrap gap-1 max-w-[60%]">
      <div
        v-for="(att, i) in attachments"
        :key="i"
        class="flex items-center gap-1.5 bg-gray-200 rounded-lg px-2 py-1"
      >
        <img :src="att.url" :alt="att.name" class="h-6 w-6 rounded object-cover" />
        <span class="text-[10px] truncate max-w-[100px]">{{ att.name || 'image' }}</span>
        <button
          type="button"
          class="p-1 text-red-600 hover:bg-red-100 rounded"
          title="Retirer"
          @click="removeAttachment(i)"
        >
          <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    </div>
    <button
      type="submit"
      class="rounded-full bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
      :disabled="sending || (!draft.trim() && attachments.length === 0)"
    >
      Envoyer
    </button>
  </form>
  <p v-else class="border-t border-gray-300 bg-gray-100 p-2 text-center text-xs text-gray-500">Lecture seule.</p>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { apolloClient } from '@/lib/apollo'
import MarkdownContent from '@/components/MarkdownContent.vue'
import { showConfirm, showToast } from '@/lib/ui'
import { usePermissionsStore } from '@/stores/permissions'
import {
  AI_ASSISTANT_MODEL_QUERY, AI_CONVERSATIONS_QUERY, AI_MESSAGES_QUERY,
  CREATE_AI_CONVERSATION, DELETE_AI_CONVERSATION, SEND_AI_MESSAGE,
} from '@/graphql/aiAssistant'

const emit = defineEmits<{ (e: 'back'): void; (e: 'close'): void }>()

const permissionsStore = usePermissionsStore()
const canView = permissionsStore.hasPermission('assistant-ia', 'view')

const model = ref<string | null>(null)
const conversationId = ref<number | null>(null)
const messages = ref<any[]>([])
const draft = ref('')
const sending = ref(false)
const pending = ref(false)
const errorText = ref('')
const scrollEl = ref<HTMLElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const attachments = ref<Array<{ url: string; name: string }>>([])

const scrollDown = () => nextTick(() => { if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight })

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = reject
  })

const onFilesSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (!files || files.length === 0) return
  for (const file of Array.from(files)) {
    if (!file.type.startsWith('image/')) continue
    if (file.size > 8 * 1024 * 1024) {
      showToast('error', `${file.name} dépasse 8 Mo et ne peut être jointe.`)
      continue
    }
    try {
      const url = await fileToBase64(file)
      attachments.value.push({ url, name: file.name })
    } catch {
      showToast('error', `Impossible de lire ${file.name}.`)
    }
  }
  input.value = ''
}

const removeAttachment = (index: number) => {
  attachments.value.splice(index, 1)
}

const loadMessages = async (id: number) => {
  const { data }: any = await apolloClient.query({ query: AI_MESSAGES_QUERY, variables: { conversationId: id }, fetchPolicy: 'network-only' })
  // Copie : les resultats Apollo sont geles et non extensibles.
  messages.value = [...(data.aiMessages ?? [])]
  await scrollDown()
}

// Reprend la conversation la plus recente ; sinon etat vide (aucun message automatique).
onMounted(async () => {
  try {
    const m: any = await apolloClient.query({ query: AI_ASSISTANT_MODEL_QUERY, fetchPolicy: 'network-only' })
    model.value = m.data.aiAssistantModel
    const { data }: any = await apolloClient.query({ query: AI_CONVERSATIONS_QUERY, fetchPolicy: 'network-only' })
    const last = data.aiConversations[0]
    if (last) { conversationId.value = last.id; await loadMessages(last.id) }
  } catch (e) {
    console.error(e)
    errorText.value = "Impossible de charger l'assistant."
  }
})

const newConversation = () => {
  conversationId.value = null
  messages.value = []
  errorText.value = ''
  draft.value = ''
  attachments.value = []
}

const removeConversation = async () => {
  if (!conversationId.value) return
  if (!(await showConfirm({ title: 'Supprimer la conversation', message: 'Supprimer cette conversation ?', variant: 'danger', confirmText: 'Supprimer' }))) return
  try {
    await apolloClient.mutate({ mutation: DELETE_AI_CONVERSATION, variables: { id: conversationId.value } })
    newConversation()
  } catch (e) {
    console.error(e)
    showToast('error', 'Impossible de supprimer la conversation')
  }
}

const send = async () => {
  const content = draft.value.trim()
  if ((!content && attachments.value.length === 0) || sending.value) return
  sending.value = true
  errorText.value = ''
  const atts = [...attachments.value]
  draft.value = ''
  attachments.value = []
  // Bulle utilisateur immediate, remplacee par la version persistee a la reponse.
  const tempId = -Date.now()
  messages.value.push({ id: tempId, role: 'USER', content, attachments: atts.map(a => ({ url: a.url })) })
  pending.value = true
  await scrollDown()
  try {
    const { data }: any = await apolloClient.mutate({ mutation: SEND_AI_MESSAGE, variables: { conversationId: conversationId.value, content, attachments: atts.map(a => a.url) } })
    const r = data.sendAiMessage
    messages.value = messages.value.filter((m) => m.id !== tempId)
    messages.value.push(r.userMessage)
    if (r.assistantMessage) messages.value.push(r.assistantMessage)
    if (!conversationId.value) {
      const list: any = await apolloClient.query({ query: AI_CONVERSATIONS_QUERY, fetchPolicy: 'network-only' })
      conversationId.value = list.data.aiConversations[0]?.id ?? null
    }
    if (!r.ok) errorText.value = r.error || "L'assistant n'a pas pu répondre."
  } catch (e: any) {
    messages.value = messages.value.filter((m) => m.id !== tempId)
    draft.value = content
    attachments.value = atts
    errorText.value = e?.graphQLErrors?.[0]?.message || e?.message || "L'assistant n'a pas pu répondre."
  } finally {
    pending.value = false
    sending.value = false
    await scrollDown()
  }
}
</script>

<style scoped>
@keyframes aiBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
</style>
